import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './DegenDashboard.css';
import './DegenDailyReport.css';
import { formatIstDateTime, formatIstDateTimeFromParts, formatIstDateKey, formatIstDateRangeLabel } from '../ist-datetime';
import IstClock from '../IstClock';

function fmt(n) {
  return `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
}

function fmtPrice(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `₹${Number(n).toFixed(2)}`;
}

function fmtPct(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
}

function fmtProfitRs(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  return `${v >= 0 ? '+' : ''}₹${Math.abs(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

function fmtSignedMoney(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  const sign = v > 0 ? '+' : v < 0 ? '−' : '';
  return `${sign}₹${Math.abs(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function fmtCharge(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function otherCharges(t) {
  if (!t || (t.totalCharges == null && t.exchangeCharges == null)) return null;
  return (
    (Number(t.exchangeCharges) || 0)
    + (Number(t.sebiCharges) || 0)
    + (Number(t.stampDuty) || 0)
    + (Number(t.dpCharges) || 0)
    + (Number(t.gst) || 0)
  );
}

/** jsPDF Helvetica cannot render ₹ and some Unicode — use ASCII for PDF cells */
function pdfText(value, fallback = '-') {
  if (value == null || value === '') return fallback;
  return String(value)
    .replace(/₹/g, 'Rs ')
    .replace(/↓/g, 'SHORT')
    .replace(/[\u2013\u2014·]/g, '-');
}

function pdfMoney(n) {
  if (n == null || n === '') return '-';
  const num = Number(n);
  if (!Number.isFinite(num)) return pdfText(n);
  if (num === 0) return 'Rs 0';
  const sign = num > 0 ? '+' : '-';
  return `${sign}Rs ${Math.round(Math.abs(num)).toLocaleString('en-IN')}`;
}

function pdfCharge(n) {
  if (n == null || n === '' || !Number.isFinite(Number(n))) return '-';
  return `Rs ${Number(n).toFixed(2)}`;
}

function istToday() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

const SCREEN_OPTIONS = [
  { key: '', label: 'All screens' },
  { key: 'options', label: 'Options Trading' },
  { key: 'normal', label: 'Normal Trading V1' },
  { key: 'degen', label: 'Degen Intraday' },
  { key: 'cnc', label: 'CNC Delivery' },
  { key: 'twohour', label: 'Two Hour Trading' },
  { key: 'future', label: 'Future Trading' }
];

const SCREEN_LINKS = {
  options: '/',
  normal: '/NormalTrading',
  degen: '/DegenDashboard',
  cnc: '/CncDegenDashboard',
  twohour: '/TwoHourTrading',
  future: '/FutureTrading'
};

export default function DegenDailyReport() {
  const [data, setData] = useState(null);
  const [allScreens, setAllScreens] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);
  const [draftScreen, setDraftScreen] = useState('');
  const [draftDate, setDraftDate] = useState(istToday);
  const [appliedScreen, setAppliedScreen] = useState('');
  const [appliedDate, setAppliedDate] = useState(istToday());
  const [showPnlOnly, setShowPnlOnly] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfFromDate, setPdfFromDate] = useState(istToday());
  const [pdfToDate, setPdfToDate] = useState(istToday());
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewed, setViewed] = useState(false);

  const appliedScreenLabel = useMemo(
    () => SCREEN_OPTIONS.find((o) => o.key === appliedScreen)?.label || 'All screens',
    [appliedScreen]
  );

  const loadAllScreens = useCallback(async (date, screen) => {
    const params = {};
    if (date) params.date = date;
    if (screen) params.screen = screen;
    const { data: report } = await axios.get('/api/reports/all-screens', {
      params,
      timeout: 20000
    });
    setAllScreens(report);
    return report;
  }, []);

  const fetchAllScreensForPdf = useCallback(async (fromDate, toDate, screenKey) => {
    const params = {};
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;
    if (screenKey) params.screen = screenKey;
    const { data: report } = await axios.get('/api/reports/all-screens', {
      params,
      timeout: 25000
    });
    return report;
  }, []);

  const downloadPdfForScreen = useCallback(async (screenKey) => {
    if (pdfBusy) return;
    setPdfBusy(true);
    setError(null);
    try {
      let fromDate = null;
      let toDate = null;
      if (pdfFromDate || pdfToDate) {
        fromDate = pdfFromDate || pdfToDate;
        toDate = pdfToDate || pdfFromDate;
      } else if (appliedDate) {
        fromDate = appliedDate;
        toDate = appliedDate;
      }
      if (fromDate && toDate && fromDate > toDate) {
        setError('Invalid date range: "From" must be <= "To"');
        return;
      }

      const report = await fetchAllScreensForPdf(fromDate, toDate, screenKey || null);

      const screenLabel = screenKey
        ? SCREEN_OPTIONS.find((o) => o.key === screenKey)?.label || screenKey
        : 'All screens';
      const rangeLabel = formatIstDateRangeLabel(fromDate, toDate);

      const rows = showPnlOnly ? (report?.pnlTrades || []) : (report?.allTrades || []);

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const startX = 28;
      let y = 28;
      doc.setFontSize(13);
      doc.text('CA TRADING LEDGER — Gross P&L, Brokerage & Net Profit', startX, y);
      y += 16;
      doc.setFontSize(9);
      doc.text(`Range: ${pdfText(rangeLabel)}`, startX, y);
      y += 12;
      doc.text(`Screen: ${pdfText(screenLabel)}`, startX, y);
      y += 12;
      doc.text(`Updated: ${pdfText(report?.generatedAtIst || report?.generatedAt || '-')}`, startX, y);
      y += 12;
      doc.text(
        `Trades: ${rows.length} | Gross P&L: ${pdfMoney(report?.totalGrossPnl ?? report?.totalRealizedPnl)} | Charges: ${pdfCharge(report?.totalCharges)} | Brokerage: ${pdfCharge(report?.totalBrokerage)} | NET PROFIT: ${pdfMoney(report?.totalNetPnl ?? report?.totalRealizedPnl)}`,
        startX,
        y
      );
      y += 10;
      const openCash = rows.find((t) => t.capitalOpening != null)?.capitalOpening;
      const endCash = [...rows].sort((a, b) => (a.sortMs || 0) - (b.sortMs || 0)).filter((t) => t.capitalBalance != null).pop()?.capitalBalance;
      if (openCash != null) {
        doc.text(
          `Wallet (bank style): Opening ${pdfMoney(openCash)} | Closing ${endCash != null ? pdfMoney(endCash) : '-'} | BUY = Debit, SELL = Credit`,
          startX,
          y
        );
        y += 10;
      }
      doc.setFontSize(7);
      doc.text(
        pdfText(report?.chargeNote || 'Charges: brokerage + STT + exchange + SEBI + stamp + DP + GST (equity delivery estimate)'),
        startX,
        y
      );
      y += 14;

      const includeScreenCol = !screenKey;
      const headRow = includeScreenCol
        ? ['Sell date/time', 'Screen', 'Stock', 'Action', 'Qty', 'Buy px', 'Sell px', 'Debit', 'Credit', 'Balance', 'Brk buy', 'Brk sell', 'STT', 'Other chg', 'Total chg', 'Gross P&L', 'Net P&L', 'Reason']
        : ['Sell date/time', 'Stock', 'Action', 'Qty', 'Buy px', 'Sell px', 'Buy date/time', 'Debit', 'Credit', 'Balance', 'Brk buy', 'Brk sell', 'STT', 'Other chg', 'Total chg', 'Gross P&L', 'Net P&L', 'Reason'];

      const body = rows.length
        ? rows.map((t) => {
          const action = `${t.action}${t.side === 'SHORT' ? ' SHORT' : ''}`;
          const sellWhen = pdfText(t.sellDateTime || t.dateTime || formatIstDateTimeFromParts(t.date, t.time));
          const buyWhen = pdfText(t.buyDateTime || '-');
          const botPx = t.botPrice != null ? `Rs ${Number(t.botPrice).toFixed(2)}` : (t.action === 'BUY' || t.action === 'SHORT' ? `Rs ${Number(t.price).toFixed(2)}` : '-');
          const sellPx = t.sellPrice != null ? `Rs ${Number(t.sellPrice).toFixed(2)}` : (t.isClose ? `Rs ${Number(t.price).toFixed(2)}` : '-');
          const gross = t.grossPnl != null ? pdfMoney(t.grossPnl) : (t.profitRs != null ? pdfMoney(t.profitRs) : (t.pnl != null ? pdfMoney(t.pnl) : '-'));
          const debit = t.capitalDebit != null ? pdfMoney(-Math.abs(Number(t.capitalDebit))) : '-';
          const credit = t.capitalCredit != null ? pdfMoney(Number(t.capitalCredit)) : '-';
          const bal = t.capitalBalance != null ? pdfMoney(t.capitalBalance) : '-';
          const otherChg = (t.exchangeCharges != null || t.stampDuty != null || t.dpCharges != null || t.gst != null || t.sebiCharges != null)
            ? pdfCharge(
              (Number(t.exchangeCharges) || 0)
              + (Number(t.sebiCharges) || 0)
              + (Number(t.stampDuty) || 0)
              + (Number(t.dpCharges) || 0)
              + (Number(t.gst) || 0)
            )
            : '-';
          if (includeScreenCol) {
            return [
              sellWhen,
              pdfText(t.screen),
              pdfText(t.stock),
              pdfText(action),
              String(t.qty ?? ''),
              botPx,
              sellPx,
              debit,
              credit,
              bal,
              pdfCharge(t.brokerageBuy),
              pdfCharge(t.brokerageSell),
              pdfCharge(t.stt),
              otherChg,
              pdfCharge(t.totalCharges),
              gross,
              t.netPnl != null ? pdfMoney(t.netPnl) : '-',
              pdfText(t.reason)
            ];
          }
          return [
            sellWhen,
            pdfText(t.stock),
            pdfText(action),
            String(t.qty ?? ''),
            botPx,
            sellPx,
            buyWhen,
            debit,
            credit,
            bal,
            pdfCharge(t.brokerageBuy),
            pdfCharge(t.brokerageSell),
            pdfCharge(t.stt),
            otherChg,
            pdfCharge(t.totalCharges),
            gross,
            t.netPnl != null ? pdfMoney(t.netPnl) : '-',
            pdfText(t.reason)
          ];
        })
        : [[
          pdfText(
            rows.length === 0
              ? `No trades for ${rangeLabel}${screenKey ? ` on ${screenLabel}` : ''}. Set PDF from/to dates and click View first.`
              : 'No rows'
          ),
          ...Array(headRow.length - 1).fill('')
        ]];

      autoTable(doc, {
        startY: y,
        head: [headRow],
        body,
        theme: 'grid',
        styles: { fontSize: 6, cellPadding: 1.5, overflow: 'linebreak' },
        headStyles: { fillColor: [13, 110, 79] },
        columnStyles: includeScreenCol ? { 1: { cellWidth: 58 } } : {},
        pageBreak: 'auto'
      });

      const finalY = (doc.lastAutoTable && doc.lastAutoTable.finalY) || y + 40;
      doc.setFontSize(8);
      doc.text(
        `SUMMARY — Gross: ${pdfMoney(report?.totalGrossPnl ?? report?.totalRealizedPnl)}  |  Brokerage: ${pdfCharge(report?.totalBrokerage)}  |  STT: ${pdfCharge(report?.totalStt)}  |  GST+Exch+SEBI+Stamp+DP: ${pdfCharge(
          (Number(report?.totalGst) || 0)
          + (Number(report?.totalExchange) || 0)
          + (Number(report?.totalSebi) || 0)
          + (Number(report?.totalStampDuty) || 0)
          + (Number(report?.totalDp) || 0)
        )}  |  Total charges: ${pdfCharge(report?.totalCharges)}  |  NET PROFIT: ${pdfMoney(report?.totalNetPnl ?? report?.totalRealizedPnl)}`,
        startX,
        Math.min(finalY + 16, 560)
      );

      const safeFrom = fromDate ? fromDate.replace(/-/g, '') : 'all';
      const safeTo = toDate ? toDate.replace(/-/g, '') : '';
      const fileScreen = screenKey ? screenKey : 'all';
      const fileName = `ca-trading-ledger_${fileScreen}_${safeFrom}${safeTo ? '_' + safeTo : ''}.pdf`;
      doc.save(fileName);
    } catch (e) {
      setError(e.message || 'PDF generation failed');
    } finally {
      setPdfBusy(false);
    }
  }, [fetchAllScreensForPdf, pdfBusy, pdfFromDate, pdfToDate, appliedDate, showPnlOnly]);

  const loadDegenMeta = useCallback(async () => {
    const { data: report } = await axios.get('/api/degen/daily-report', { timeout: 15000 });
    setData(report);
    return report;
  }, []);

  const loadDegenDay = useCallback(async (date) => {
    if (!date) {
      setDayDetail(null);
      return null;
    }
    const { data: report } = await axios.get('/api/degen/daily-report', {
      params: { date },
      timeout: 15000
    });
    setDayDetail(report);
    return report;
  }, []);

  const handleView = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const date = draftDate || null;
      const screen = draftScreen || null;
      setAppliedScreen(screen || '');
      setAppliedDate(date);
      if (date) {
        setPdfFromDate(date);
        setPdfToDate(date);
      } else {
        setPdfFromDate('');
        setPdfToDate('');
      }
      await loadAllScreens(date, screen);
      if (!screen || screen === 'degen') {
        await loadDegenDay(date);
      } else {
        setDayDetail(null);
      }
      setViewed(true);
    } catch (e) {
      setError(e.message || 'Could not load report');
    } finally {
      setBusy(false);
    }
  }, [draftDate, draftScreen, loadAllScreens, loadDegenDay]);

  useEffect(() => {
    const today = istToday();
    setDraftDate(today);
    setPdfFromDate(today);
    setPdfToDate(today);
    setAppliedDate(today);
    (async () => {
      try {
        await loadDegenMeta();
        await loadAllScreens(today, '');
        setViewed(true);
      } catch (e) {
        setError(e.message || 'Could not load report');
      }
    })();
  }, [loadAllScreens, loadDegenMeta]);

  const saveEod = async () => {
    setBusy(true);
    try {
      await axios.post('/api/degen/daily-report/save-eod');
      await loadDegenMeta();
      await loadAllScreens(appliedDate, appliedScreen || null);
      if (appliedDate) await loadDegenDay(appliedDate);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const tradeRows = useMemo(() => {
    if (!allScreens) return [];
    return showPnlOnly ? allScreens.pnlTrades : allScreens.allTrades;
  }, [allScreens, showPnlOnly]);

  const screenSummary = allScreens?.selectedScreenSummary;
  const showDegenDetail = !appliedScreen || appliedScreen === 'degen';
  const today = dayDetail || (appliedDate ? null : data?.today);
  const summary = today?.summary;
  const appliedDateLabel = appliedDate ? formatIstDateKey(appliedDate) : 'All dates';

  const setToday = () => {
    const today = istToday();
    setDraftDate(today);
    setPdfFromDate(today);
    setPdfToDate(today);
  };
  const setAllDates = () => {
    setDraftDate('');
    setPdfFromDate('');
    setPdfToDate('');
  };

  const applyDayFromTable = (date, screen = appliedScreen) => {
    setDraftDate(date);
    setDraftScreen(screen || '');
    setBusy(true);
    setError(null);
    setAppliedScreen(screen || '');
    setAppliedDate(date);
    if (date) {
      setPdfFromDate(date);
      setPdfToDate(date);
    }
    loadAllScreens(date, screen || null)
      .then(() => {
        if (!screen || screen === 'degen') return loadDegenDay(date);
        setDayDetail(null);
        return null;
      })
      .catch((e) => setError(e.message || 'Could not load report'))
      .finally(() => setBusy(false));
  };

  return (
    <div className="degen-app degen-report-app">
      <header className="degen-topbar">
        <div className="degen-top-left">
          <span className="degen-tag degen-tag-mode">CA TRADING LEDGER</span>
          <span className="degen-tag degen-tag-pipe">GROSS · BROKERAGE · NET PROFIT</span>
        </div>
        <div className="degen-top-right">
          <IstClock />
        </div>
      </header>

      {error && <div className="degen-error-banner">{error}</div>}

      <section className="degen-card degen-report-toolbar">
        <label className="degen-report-screen-select" htmlFor="report-screen-select">
          <span>Select screen</span>
          <select
            id="report-screen-select"
            value={draftScreen}
            onChange={(e) => setDraftScreen(e.target.value)}
          >
            {SCREEN_OPTIONS.map((opt) => (
              <option key={opt.key || 'all'} value={opt.key}>{opt.label}</option>
            ))}
          </select>
        </label>

        <label className="degen-report-screen-select" htmlFor="report-date-select">
          <span>Select date &amp; time</span>
          <input
            id="report-date-select"
            type="date"
            value={draftDate}
            onChange={(e) => setDraftDate(e.target.value)}
          />
        </label>

        <div className="degen-report-date-quick">
          <button type="button" className="degen-report-quick-btn" onClick={setToday}>
            Today
          </button>
          <button type="button" className="degen-report-quick-btn" onClick={setAllDates}>
            All dates
          </button>
        </div>

        <label className="degen-report-screen-select" htmlFor="pdf-from-select">
          <span>PDF from</span>
          <input
            id="pdf-from-select"
            type="date"
            value={pdfFromDate}
            onChange={(e) => setPdfFromDate(e.target.value)}
          />
        </label>

        <label className="degen-report-screen-select" htmlFor="pdf-to-select">
          <span>PDF to</span>
          <input
            id="pdf-to-select"
            type="date"
            value={pdfToDate}
            onChange={(e) => setPdfToDate(e.target.value)}
          />
        </label>

        <button
          type="button"
          className="degen-report-view-btn"
          onClick={handleView}
          disabled={busy}
        >
          {busy ? 'Loading…' : 'View'}
        </button>

        {appliedScreen && SCREEN_LINKS[appliedScreen] && viewed && (
          <Link to={SCREEN_LINKS[appliedScreen]} className="degen-report-open-screen">
            Open {appliedScreenLabel} →
          </Link>
        )}
      </section>

      {viewed && allScreens?.optionsTomorrow?.ok && (
        <section className="degen-card degen-options-tomorrow" aria-label="Options session plan">
          <header className="degen-options-tomorrow-head">
            <div>
              <h2>{allScreens.optionsTomorrow.title}</h2>
              <p>{allScreens.optionsTomorrow.sessionLabel || allScreens.optionsTomorrow.tomorrowLabel}</p>
            </div>
            <Link to="/" className="degen-report-open-screen">
              Open Options →
            </Link>
          </header>
          <p className="degen-options-tomorrow-summary">
            {allScreens.optionsTomorrow.plainSummary}
          </p>
          <div className="degen-report-grid">
            <div>
              <span>Day profit goal</span>
              <strong className="up">
                {fmt(allScreens.optionsTomorrow.controls?.dailyTarget)}
              </strong>
            </div>
            <div>
              <span>Day margin</span>
              <strong>{fmt(allScreens.optionsTomorrow.controls?.maxDailyInvestment)}</strong>
            </div>
            <div>
              <span>Stop / trade</span>
              <strong>
                {allScreens.optionsTomorrow.controls?.zeroLossOnly
                  ? '0% hold'
                  : fmt(allScreens.optionsTomorrow.controls?.stopLossPerTrade)}
              </strong>
            </div>
            <div>
              <span>Take profit / trade</span>
              <strong className="up">
                {fmt(allScreens.optionsTomorrow.controls?.takeProfitPerTrade)}
              </strong>
            </div>
            <div>
              <span>Lean for next session</span>
              <strong>
                {allScreens.optionsTomorrow.bias?.label || '—'}
                {allScreens.optionsTomorrow.bias?.confidence != null
                  ? ` · ${allScreens.optionsTomorrow.bias.confidence}%`
                  : ''}
              </strong>
            </div>
            <div>
              <span>Recheck</span>
              <strong>Every {allScreens.optionsTomorrow.controls?.pollMinutes || 2} min</strong>
            </div>
          </div>
          <ol className="degen-options-tomorrow-steps">
            {(allScreens.optionsTomorrow.steps || []).map((step) => (
              <li key={`${step.when}-${step.what}`}>
                <strong>{step.when}</strong>
                <span>{step.what}</span>
              </li>
            ))}
          </ol>
          {(allScreens.optionsTomorrow.tips || []).length > 0 && (
            <ul className="degen-options-tomorrow-tips">
              {allScreens.optionsTomorrow.tips.map((tip) => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      {viewed && allScreens && (
        <>
          <section className="degen-card degen-report-hero">
            <h1>
              {appliedScreen
                ? `${appliedScreenLabel} — CA ledger`
                : 'CA Trading Ledger — all screens'}
            </h1>
            <p>
              {appliedDate ? appliedDateLabel : 'All dates'}
              {appliedScreen ? ` · ${appliedScreenLabel}` : ' · all screens'}
              {' · '}
              {allScreens.totalTrades} trades · {allScreens.totalPnlTrades} with P&L
              {' · '}updated {allScreens.generatedAtIst}
            </p>
            <div className="degen-report-grid degen-ledger-hero-grid">
              <div>
                <span>Gross P&amp;L</span>
                <strong className={(allScreens.totalGrossPnl ?? allScreens.totalRealizedPnl) >= 0 ? 'up' : 'down'}>
                  {fmtProfitRs(allScreens.totalGrossPnl ?? allScreens.totalRealizedPnl)}
                </strong>
              </div>
              <div>
                <span>Brokerage paid</span>
                <strong>{fmtCharge(allScreens.totalBrokerage)}</strong>
              </div>
              <div>
                <span>Total charges</span>
                <strong>{fmtCharge(allScreens.totalCharges)}</strong>
              </div>
              <div>
                <span>Net profit (after charges)</span>
                <strong className={(allScreens.totalNetPnl ?? allScreens.totalRealizedPnl) >= 0 ? 'up' : 'down'}>
                  {fmtProfitRs(allScreens.totalNetPnl ?? allScreens.totalRealizedPnl)}
                </strong>
              </div>
              {(() => {
                const rows = allScreens.allTrades || [];
                const open = rows.find((t) => t.capitalOpening != null)?.capitalOpening;
                if (open == null) return null;
                const end = [...rows]
                  .filter((t) => t.capitalBalance != null)
                  .sort((a, b) => (a.sortMs || 0) - (b.sortMs || 0))
                  .pop()?.capitalBalance;
                return (
                  <>
                    <div>
                      <span>Wallet opening</span>
                      <strong>{fmt(open)}</strong>
                    </div>
                    <div>
                      <span>Wallet closing</span>
                      <strong className={end == null ? '' : end >= open ? 'up' : 'down'}>
                        {end != null ? fmt(end) : '—'}
                      </strong>
                    </div>
                  </>
                );
              })()}
            </div>
            <p className="degen-report-note degen-ledger-note">
              Bank-style wallet: starts at Master capital · BUY = Debit · SELL = Credit (money back + profit/loss) · Balance = cash left
            </p>
            {allScreens.chargeNote && (
              <p className="degen-report-note degen-ledger-note">{allScreens.chargeNote}</p>
            )}
            {appliedDate && allScreens.totalTrades === 0 && (
              <p className="degen-report-note">
                No trades recorded for {appliedDateLabel}
                {appliedScreen ? ` on ${appliedScreenLabel}` : ''}.
                Pick another date or screen and click View.
              </p>
            )}
          </section>

          {appliedScreen && screenSummary ? (
            <section className="degen-card degen-report-summary">
              <h2>
                {appliedScreenLabel} — summary
                {appliedDate ? ` · ${appliedDateLabel}` : ''}
              </h2>
              <div className="degen-report-pdf-actions">
                <button
                  type="button"
                  className="degen-report-pdf-btn"
                  onClick={() => downloadPdfForScreen(appliedScreen)}
                  disabled={pdfBusy}
                >
                  {pdfBusy ? 'Generating PDF…' : 'Download PDF'}
                </button>
              </div>
              <div className="degen-report-grid">
                <div>
                  <span>Gross P&amp;L</span>
                  <strong className={(screenSummary.grossPnl ?? screenSummary.realizedPnl) >= 0 ? 'up' : 'down'}>
                    {fmtProfitRs(screenSummary.grossPnl ?? screenSummary.realizedPnl)}
                  </strong>
                </div>
                <div>
                  <span>Brokerage</span>
                  <strong>{fmtCharge(screenSummary.brokerageTotal)}</strong>
                </div>
                <div>
                  <span>Total charges</span>
                  <strong>{fmtCharge(screenSummary.totalCharges)}</strong>
                </div>
                <div>
                  <span>Net profit</span>
                  <strong className={(screenSummary.netPnl ?? screenSummary.realizedPnl) >= 0 ? 'up' : 'down'}>
                    {fmtProfitRs(screenSummary.netPnl ?? screenSummary.realizedPnl)}
                  </strong>
                </div>
                <div><span>Total orders</span><strong>{screenSummary.tradeCount}</strong></div>
                <div><span>Exits with P&amp;L</span><strong>{screenSummary.closeCount}</strong></div>
                <div><span>Wins / Losses</span><strong>{screenSummary.wins} / {screenSummary.losses}</strong></div>
                <div><span>Profit from wins</span><strong className="up">+{fmt(screenSummary.winPnl)}</strong></div>
                <div><span>Loss from losers</span><strong className="down">{fmt(screenSummary.lossPnl)}</strong></div>
              </div>
              {(allScreens.totalStt != null || allScreens.totalGst != null) && (
                <div className="degen-ledger-charge-break">
                  <span>STT {fmtCharge(allScreens.totalStt)}</span>
                  <span>Exchange {fmtCharge(allScreens.totalExchange)}</span>
                  <span>SEBI {fmtCharge(allScreens.totalSebi)}</span>
                  <span>Stamp {fmtCharge(allScreens.totalStampDuty)}</span>
                  <span>DP {fmtCharge(allScreens.totalDp)}</span>
                  <span>GST {fmtCharge(allScreens.totalGst)}</span>
                </div>
              )}
            </section>
          ) : (
            <section className="degen-card degen-report-summary">
              <h2>Profit & loss by screen{appliedDate ? ` · ${appliedDateLabel}` : ''}</h2>
              <div className="degen-report-grid degen-screen-summary-grid">
                {allScreens.screens.map((s) => (
                  <div key={s.screenKey} className="degen-screen-card-row">
                    <button
                      type="button"
                      className="degen-screen-card degen-screen-card-btn"
                      onClick={() => {
                        setDraftScreen(s.screenKey);
                        setAppliedScreen(s.screenKey);
                        setBusy(true);
                        loadAllScreens(appliedDate, s.screenKey)
                          .then(() => {
                            if (s.screenKey === 'degen' && appliedDate) return loadDegenDay(appliedDate);
                            setDayDetail(null);
                            return null;
                          })
                          .catch((e) => setError(e.message))
                          .finally(() => setBusy(false));
                      }}
                    >
                      <span>{s.screen}</span>
                      <strong className={(s.netPnl ?? s.realizedPnl) >= 0 ? 'up' : 'down'}>
                        Net {fmtProfitRs(s.netPnl ?? s.realizedPnl)}
                      </strong>
                      <em>
                        Gross {fmtProfitRs(s.grossPnl ?? s.realizedPnl)}
                        {s.totalCharges != null ? ` · Chg ${fmtCharge(s.totalCharges)}` : ''}
                        {' · '}{s.closeCount} exits · {s.wins}W / {s.losses}L
                      </em>
                    </button>

                    <button
                      type="button"
                      className="degen-report-pdf-btn"
                      onClick={() => downloadPdfForScreen(s.screenKey)}
                      disabled={pdfBusy}
                      title="Download PDF for this screen"
                    >
                      PDF
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {allScreens.days?.length > 0 && (
            <section className="degen-card">
              <h2>
                Daily totals
                {appliedScreen ? ` — ${appliedScreenLabel}` : ' (all screens)'}
              </h2>
              <div className="degen-report-table-wrap">
                <table className="degen-scan-table degen-report-table">
                  <thead>
                    <tr>
                      <th>Date &amp; time</th>
                      <th>Gross P&amp;L</th>
                      <th>Charges</th>
                      <th>Net P&amp;L</th>
                      <th>Trades</th>
                      {!appliedScreen && <th>By screen</th>}
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {allScreens.days.map((d) => (
                      <tr key={d.date} className={d.date === appliedDate ? 'qualifies' : ''}>
                        <td>{d.dateTime || formatIstDateKey(d.date)}</td>
                        <td className={(d.totalGrossPnl ?? d.totalPnl) >= 0 ? 'up' : 'down'}>
                          {fmtProfitRs(d.totalGrossPnl ?? d.totalPnl)}
                        </td>
                        <td>{fmtCharge(d.totalCharges)}</td>
                        <td className={(d.totalNetPnl ?? d.totalPnl) >= 0 ? 'up' : 'down'}>
                          {fmtProfitRs(d.totalNetPnl ?? d.totalPnl)}
                        </td>
                        <td>{d.tradeCount}</td>
                        {!appliedScreen && (
                          <td className="degen-day-screens">
                            {d.screens.map((s) => (
                              <button
                                key={s.screenKey}
                                type="button"
                                className={`degen-day-screen-chip ${(s.netPnl ?? s.pnl) >= 0 ? 'up' : 'down'}`}
                                onClick={() => {
                                  setDraftScreen(s.screenKey);
                                  setDraftDate(d.date);
                                  setAppliedScreen(s.screenKey);
                                  setAppliedDate(d.date);
                                  setBusy(true);
                                  loadAllScreens(d.date, s.screenKey)
                                    .then(() => {
                                      if (s.screenKey === 'degen') return loadDegenDay(d.date);
                                      setDayDetail(null);
                                      return null;
                                    })
                                    .catch((e) => setError(e.message))
                                    .finally(() => setBusy(false));
                                }}
                              >
                                {s.screen.split(' ')[0]} {fmtProfitRs(s.netPnl ?? s.pnl)}
                              </button>
                            ))}
                          </td>
                        )}
                        <td>
                          <button
                            type="button"
                            className="degen-report-link-btn"
                            onClick={() => applyDayFromTable(d.date)}
                          >
                            View day
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section className="degen-card">
            <header className="degen-report-table-head">
              <h2>
                CA ledger — {appliedScreenLabel}
                {appliedDate ? ` · ${appliedDateLabel}` : ' · all dates'}
              </h2>
              <div className="degen-report-filters">
                <button
                  type="button"
                  className={`degen-report-filter-btn ${showPnlOnly ? 'active' : ''}`}
                  onClick={() => setShowPnlOnly((v) => !v)}
                >
                  P&L exits only
                </button>
              </div>
            </header>
            <div className="degen-report-table-wrap">
              <table className="degen-scan-table degen-report-table degen-ledger-table">
                <thead>
                  <tr>
                    <th>Sell date &amp; time</th>
                    {!appliedScreen && <th>Screen</th>}
                    <th>Stock</th>
                    <th>Exch</th>
                    <th>Action</th>
                    <th>Qty</th>
                    <th>Bot / Buy px</th>
                    <th>Sell px</th>
                    <th>Buy date &amp; time</th>
                    <th title="Cash out on BUY (from ₹50k wallet)">Debit</th>
                    <th title="Cash in on SELL (capital + profit/loss)">Credit</th>
                    <th title="Wallet balance after this row (starts at Master capital)">Balance</th>
                    <th>Brk buy</th>
                    <th>Brk sell</th>
                    <th>STT</th>
                    <th>Other chg</th>
                    <th>Total chg</th>
                    <th>Gross P&amp;L</th>
                    <th>Net P&amp;L</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {tradeRows.length === 0 ? (
                    <tr>
                      <td colSpan={appliedScreen ? 19 : 20} className="degen-action-empty">
                        No trades for {appliedScreenLabel}
                        {appliedDate ? ` on ${appliedDateLabel}` : ''}
                      </td>
                    </tr>
                  ) : (
                    tradeRows.map((t, i) => {
                      const gross = t.grossPnl ?? t.profitRs ?? t.pnl;
                      const net = t.netPnl;
                      const bal = t.capitalBalance;
                      const openBal = t.capitalOpening;
                      return (
                        <tr key={`${t.screenKey}-${t.symbol}-${t.sortMs}-${i}`} className={t.hasPnl && (net ?? gross) >= 0 ? 'qualifies' : ''}>
                          <td>{t.sellDateTime || t.dateTime || formatIstDateTimeFromParts(t.date, t.time)}</td>
                          {!appliedScreen && <td>{t.screen}</td>}
                          <td>{t.stock}</td>
                          <td>{t.exchange}</td>
                          <td>{t.action}{t.side === 'SHORT' ? ' ↓' : ''}</td>
                          <td>{t.qty}</td>
                          <td>{fmtPrice(t.botPrice ?? t.buyPrice ?? (['BUY', 'SHORT'].includes(t.action) ? t.price : null))}</td>
                          <td>{fmtPrice(t.sellPrice ?? (t.isClose ? t.price : null))}</td>
                          <td>{t.buyDateTime || '—'}</td>
                          <td className="down">
                            {t.capitalDebit != null ? fmtSignedMoney(-Math.abs(Number(t.capitalDebit))) : '—'}
                          </td>
                          <td className="up">
                            {t.capitalCredit != null ? fmtSignedMoney(Number(t.capitalCredit)) : '—'}
                          </td>
                          <td
                            className={bal == null || openBal == null ? '' : bal >= openBal ? 'up' : 'down'}
                            title={openBal != null ? `Opening wallet ₹${Number(openBal).toLocaleString('en-IN')}` : undefined}
                          >
                            {bal != null ? fmt(bal) : '—'}
                          </td>
                          <td>{fmtCharge(t.brokerageBuy)}</td>
                          <td>{fmtCharge(t.brokerageSell)}</td>
                          <td>{fmtCharge(t.stt)}</td>
                          <td>{fmtCharge(otherCharges(t))}</td>
                          <td>{fmtCharge(t.totalCharges)}</td>
                          <td className={gross == null ? '' : gross >= 0 ? 'up' : 'down'}>
                            {fmtProfitRs(gross)}
                          </td>
                          <td className={net == null ? '' : net >= 0 ? 'up' : 'down'}>
                            {fmtProfitRs(net)}
                          </td>
                          <td>{t.reason}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {showDegenDetail && viewed && (
        <section className="degen-card degen-report-degen-section">
          <h2>Degen intraday — detailed day report</h2>
          {data && (
            <div className="degen-report-hero degen-report-hero-nested">
              <p>
                Lifetime: {fmt(data.totalLifetimeProfit)} · Equity {fmt(data.currentEquity ?? data.currentCash)} ·
                {data.tradeLogCount} trades in log
              </p>
              <button type="button" className="degen-toggle" onClick={saveEod} disabled={busy}>
                {busy ? 'Saving…' : 'Save Degen end-of-day report'}
              </button>
            </div>
          )}

          {summary && (
            <div className="degen-report-summary">
              <h3>{today.label || appliedDateLabel} — Degen summary</h3>
              <div className="degen-report-grid">
                <div><span>Net profit</span><strong className={summary.netProfit >= 0 ? 'up' : 'down'}>{fmt(summary.netProfit)}</strong></div>
                <div><span>Round trips</span><strong>{summary.totalRoundTrips}</strong></div>
                <div><span>Wins / Losses</span><strong>{summary.wins} / {summary.losses}</strong></div>
                <div><span>Win rate</span><strong>{summary.winRate}%</strong></div>
              </div>
            </div>
          )}

          {data?.days?.length > 0 && (
            <div className="degen-report-table-wrap">
              <table className="degen-scan-table degen-report-table">
                <thead>
                  <tr>
                    <th>Date &amp; time</th>
                    <th>Net profit</th>
                    <th>W/L</th>
                    <th>Round trips</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.days.map((d) => (
                    <tr key={d.date} className={d.date === appliedDate ? 'qualifies' : ''}>
                      <td>{d.dateTime || formatIstDateKey(d.date || d.label)}</td>
                      <td className={d.netProfit >= 0 ? 'up' : 'down'}>{fmt(d.netProfit)}</td>
                      <td>{d.wins}/{d.losses}</td>
                      <td>{d.roundTrips}</td>
                      <td>
                        <button
                          type="button"
                          className="degen-report-link-btn"
                          onClick={() => applyDayFromTable(d.date, 'degen')}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
